// Cartões salvos da recorrência: PRINCIPAL (cobrado primeiro) + ordem de fallback
// (se o principal falhar, tenta o próximo). Permite tornar principal, remover,
// reordenar o fallback e adicionar cartão via SetupIntent (PaymentElement em modo setup).
import { useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Trash2, Loader2, Plus, ChevronUp, ChevronDown, Star, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { CheckoutDialog, type CheckoutTarget } from './CheckoutDialog';

export interface PaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  brand: string;
  last4: string;
  exp_month?: number;
  exp_year?: number;
  is_default?: boolean;
  sort_order?: number;
  status?: 'ok' | 'problem';
}

export function SavedCards({
  cards,
  email,
  returnUrl,
  onChanged,
}: {
  cards: PaymentMethod[];
  email: string;
  returnUrl: string;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState('');
  const [target, setTarget] = useState<CheckoutTarget | null>(null);
  // Ordem local p/ feedback imediato no reorder (espelha o servidor).
  const sorted = [...cards].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const setDefault = async (c: PaymentMethod) => {
    setBusy(`def:${c.id}`);
    try {
      const r = await apiFetch('POST', `/billing/payment-methods/${c.stripe_payment_method_id}/default`);
      if (!r.ok) { toast.error(t('billing.cardDefaultFailed')); return; }
      toast.success(t('billing.cardDefaultSet'));
      onChanged();
    } finally { setBusy(''); }
  };

  const remove = async (c: PaymentMethod) => {
    setBusy(`rm:${c.id}`);
    try {
      const r = await apiFetch('DELETE', `/billing/payment-methods/${c.stripe_payment_method_id}`);
      if (!r.ok) { toast.error(t('billing.cardDeleteFailed')); return; }
      toast.success(t('billing.cardDeleted'));
      onChanged();
    } finally { setBusy(''); }
  };

  // Reordena o fallback: troca dois vizinhos e envia a lista (1º = principal).
  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...sorted];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setBusy(`mv:${sorted[idx].id}`);
    try {
      const ids = next.map((c) => c.stripe_payment_method_id);
      const r = await apiFetch('POST', '/billing/payment-methods/reorder', { ids });
      if (!r.ok) { toast.error(t('billing.cardReorderFailed')); return; }
      onChanged();
    } finally { setBusy(''); }
  };

  // Adicionar cartão: cria um SetupIntent e abre o PaymentElement em modo setup.
  const addCard = async () => {
    setBusy('add');
    try {
      const r = await apiFetch('POST', '/billing/setup-intent', { email });
      const cs = (r.data as { client_secret?: string } | undefined)?.client_secret;
      if (!r.ok || !cs) {
        if (r.status === 501) toast.error(t('billing.notConfigured'));
        else toast.error(t('billing.cardAddFailed'));
        return;
      }
      setTarget({
        clientSecret: cs,
        mode: 'setup',
        returnUrl,
        title: t('billing.addCardTitle'),
        description: t('billing.addCardDesc'),
        confirmLabel: t('billing.addCardConfirm'),
      });
    } finally { setBusy(''); }
  };

  return (
    <div className="flex flex-col gap-2">
      {sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('billing.savedCardsEmpty')}</p>
      ) : (
        sorted.map((c, idx) => {
          const problem = c.status === 'problem';
          return (
            <div
              key={c.id}
              className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${problem ? 'border-amber-400 bg-amber-50' : ''}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                {/* Reorder do fallback (só com 2+ cartões) */}
                {sorted.length > 1 && (
                  <div className="flex flex-col">
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title={t('billing.cardMoveUp')}
                      disabled={idx === 0 || !!busy}
                      onClick={() => move(idx, -1)}
                    >
                      <ChevronUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title={t('billing.cardMoveDown')}
                      disabled={idx === sorted.length - 1 || !!busy}
                      onClick={() => move(idx, 1)}
                    >
                      <ChevronDown className="size-4" />
                    </button>
                  </div>
                )}
                <CreditCard className={`size-5 ${problem ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    <span className="capitalize">{c.brand}</span> ···· {c.last4}
                    {c.is_default && <Badge variant="default" className="gap-1"><Star className="size-3" /> {t('billing.cardPrincipal')}</Badge>}
                    {problem && <Badge variant="warning" className="gap-1"><AlertTriangle className="size-3" /> {t('billing.cardProblem')}</Badge>}
                  </p>
                  {!!c.exp_month && !!c.exp_year && (
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {String(c.exp_month).padStart(2, '0')}/{c.exp_year}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!c.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy === `def:${c.id}`}
                    onClick={() => setDefault(c)}
                    title={t('billing.cardSetPrincipal')}
                  >
                    {busy === `def:${c.id}` ? <Loader2 className="size-4 animate-spin" /> : <Star className="size-4" />}
                    <span className="hidden sm:inline">{t('billing.cardSetPrincipal')}</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={busy === `rm:${c.id}`}
                  onClick={() => remove(c)}
                  title={t('billing.cardRemove')}
                >
                  {busy === `rm:${c.id}` ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                </Button>
              </div>
            </div>
          );
        })
      )}

      {sorted.length > 1 && (
        <p className="text-xs text-muted-foreground">{t('billing.cardFallbackHint')}</p>
      )}

      <div>
        <Button variant="outline" size="sm" disabled={!!busy} onClick={addCard}>
          {busy === 'add' ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          {t('billing.addCard')}
        </Button>
      </div>

      <CheckoutDialog
        target={target}
        onClose={() => setTarget(null)}
        onSuccess={() => { setTarget(null); toast.success(t('billing.cardAdded')); onChanged(); }}
      />
    </div>
  );
}
